import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/joy';
import type { VersionPairingRow } from '../utils/versionPairings';

export function VersionPairingMap({
  rows,
  currentSurveyId,
  currentVersionId,
  onSelectSurvey,
  onSelectVersion,
}: {
  rows: VersionPairingRow[];
  currentSurveyId?: string | null;
  currentVersionId?: string | null;
  onSelectSurvey?: (surveyId: string) => void;
  onSelectVersion?: (versionId: string, documentId: string) => void;
}) {
  if (rows.length === 0) return null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'md',
        overflow: 'hidden',
        mb: 1.5,
      }}
    >
      <Box sx={{ px: 1.5, py: 1, bgcolor: 'background.level1', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography level="title-sm">How these versions line up</Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
          One Visual Designer survey belongs to one quote or estimate version. Nothing crosses.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 28px minmax(0, 1.2fr)' },
          columnGap: 1,
          rowGap: 0,
          px: 1.5,
          py: 0.5,
        }}
      >
        <Typography level="body-xs" sx={{ color: 'neutral.500', display: { xs: 'none', sm: 'block' }, py: 0.75 }}>
          Visual Designer
        </Typography>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Typography level="body-xs" sx={{ color: 'neutral.500', display: { xs: 'none', sm: 'block' }, py: 0.75 }}>
          Document version
        </Typography>
        {rows.map((row) => {
          const surveyCurrent = !!currentSurveyId && row.surveyId === currentSurveyId;
          const versionCurrent = !!currentVersionId && row.versionId === currentVersionId;
          const paired = !!(row.surveyId && row.versionId);
          return (
            <React.Fragment key={row.key}>
              <PairingCell
                label={row.surveyLabel || 'Not paired'}
                muted={!row.surveyLabel}
                current={surveyCurrent}
                onClick={row.surveyId && onSelectSurvey ? () => onSelectSurvey(row.surveyId as string) : undefined}
              />
              <Typography
                level="body-sm"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: paired ? 'primary.500' : 'neutral.400',
                  fontWeight: 'lg',
                }}
              >
                {paired ? '↔' : '·'}
              </Typography>
              <PairingCell
                label={row.versionLabel || 'Not paired'}
                muted={!row.versionLabel}
                current={versionCurrent}
                locked={row.locked}
                onClick={row.versionId && row.documentId && onSelectVersion
                  ? () => onSelectVersion(row.versionId as string, row.documentId as string)
                  : undefined}
              />
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

function PairingCell({
  label,
  muted,
  current,
  locked,
  onClick,
}: {
  label: string;
  muted?: boolean;
  current?: boolean;
  locked?: boolean;
  onClick?: () => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      } : undefined}
      sx={{
        py: 0.85,
        px: 0.5,
        borderRadius: 'sm',
        minWidth: 0,
        bgcolor: current ? 'primary.50' : 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        level="body-sm"
        fontWeight={muted ? 'md' : 'lg'}
        sx={{ color: muted ? 'neutral.400' : 'text.primary', minWidth: 0 }}
        noWrap
      >
        {label}
      </Typography>
      {current && <Chip size="sm" variant="soft" color="primary">Viewing</Chip>}
      {locked && <Chip size="sm" variant="soft" color="danger">Locked</Chip>}
    </Stack>
  );
}
